import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, elements, registerables } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css'
import { BiFullscreen, BiExitFullscreen, BiLineChart, BiBarChartAlt2 } from 'react-icons/bi';


const LprDailyCountChart = ({
    selectedYear,
    dailyLprCountData,
    dailyDataLoading,
    selectedMonth,
    selectedWeekStartDate,
    selectedDay,
    selectedDayOfWeek,
    dailyVehiclePercentageIncrease,
    dailyVehicles
}) => {
    const [barChartData, setBarChartData] = useState();
    const [lineChartData, setLineChartData] = useState();
    const [chartType, setChartType] = useState('line');
    const barChartRef = useRef(null);
    const lineChartRef = useRef(null);

    const smallScreen = window.innerWidth < 786;
    const [isFullscreen, setIsFullscreen] = useState(false);

    const getMonthName = (monthNumber) => {
        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return months[monthNumber - 1];
    };

    const prepareDailyLprCountBarChart = () => {
        // console.log("The daily lpr count data before preparing the chart::: ", dailyLprCountData);
        return {
            labels: dailyLprCountData.map(d => d.day),
            datasets: [
                {
                    label: 'Daily LPR Count',
                    data: dailyLprCountData.map(d => d.total_count),
                    backgroundColor: '#FFC400',
                    borderColor: '#FFC400',
                    borderWidth: 1,
                }
            ]
        };
    };

    
    const getDailyLprCountBarChartOptions = () => {
        return {
            responsive: true, 
            maintainAspectRatio: true, 
            hover: {
                mode: 'nearest',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            scales: {
                x: {
                    stacked: true,
                    ticks: {
                        color: (context) => {
                            const labelDay = dailyLprCountData[context.index]?.day;
                            return labelDay === selectedDayOfWeek ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => {
                                const labelDay = dailyLprCountData[context.index]?.day;
                                return labelDay === selectedDayOfWeek ? 'bold' : 'normal';
                            },
                            size: (context) => {
                                const labelDay = dailyLprCountData[context.index]?.day;
                                return labelDay === selectedDayOfWeek ? 14 : 12;
                            }
                        },
                    },
                    title: {
                        display: true,
                        text: 'Days of the week',
                        color: 'white'
                    },
                    grid: {
                        display: true,
                        drawBorder: true,
                        drawOnChartArea: false,
                        color: '#aaa'
                    }
                },
                y: {
                    ticks: {
                        color: '#F8F8F8',
                    },
                    stacked: true,
                    title: {
                        display: true,
                        text: 'LPR Count',
                        color: 'white',
                    },
                },
            },
            plugins: {
                tooltip: {
                    mode: 'nearest', 
                    intersect: 'false'
                },
                legend: {
                    position: 'top', 
                    labels: {
                        color: 'white',
                        font: {
                            size: smallScreen ? 8 : 14, 
                        },
                        boxWidth: smallScreen ? 8 : 40,  
                        boxHeight: smallScreen ? 8 : 10, 
                    },
                },
            },

        }
    }


    const getDailyLprCountLineChartOptions = () => {
        return {
            responsive: true, 
            maintainAspectRatio: true, 
            hover: {
                mode: 'nearest',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            elements: {
                points: {
                    radius: 4,
                    hoverRadius: 6
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: (context) => {
                            const labelDay = dailyLprCountData[context.index]?.day;
                            return labelDay === selectedDayOfWeek ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => {
                                const labelDay = dailyLprCountData[context.index]?.day;
                                return labelDay === selectedDayOfWeek ? 'bold' : 'normal';
                            },
                            size: (context) => {
                                const labelDay = dailyLprCountData[context.index]?.day;
                                return labelDay === selectedDayOfWeek ? 14 : 12;
                            }
                        },
                    },
                    title: {
                        display: true,
                        text: 'Days of the week',
                        color: 'white'
                    },
                    grid: {
                        display: true,
                        drawBorder: true,
                        drawOnChartArea: false,
                        color: '#aaa'
                    }
                },
                y: {
                    ticks: {
                        color: '#F8F8F8',
                    },
                    title: {
                        display: true,
                        text: 'LPR Count',
                        color: 'white',
                    },
                },
            },
            plugins: {
                tooltip: {
                    mode: 'nearest', 
                    intersect: 'false'
                },
                legend: {
                    position: 'top', 
                    labels: {
                        color: 'white',
                        font: {
                            size: smallScreen ? 8 : 14, 
                        },
                        boxWidth: smallScreen ? 8 : 40,  
                        boxHeight: smallScreen ? 8 : 10, 
                    },
                },
            },
        }
    }

    const prepareDailyLprCountLineChart = () => {
        // console.log("The daily lpr count data before preparing the chart::: ", dailyLprCountData);
        return {
            labels: dailyLprCountData.map(d => d.day),
            datasets: [
                {
                    label: 'Daily LPR Count',
                    data: dailyLprCountData.map(d => d.total_count),
                    backgroundColor: '#FFC400',
                    borderColor: '#FFC400',
                    borderWidth: 2,
                    tension: 0.3
                }
            ]
        };
    };

    const toggleChartType = () => {
        setChartType(prevType => prevType === 'bar' ? 'line' : 'bar');
    };

    useEffect(() => {
        if (dailyLprCountData.length) {
            const preparedLineChart = prepareDailyLprCountLineChart();
            if (preparedLineChart) {
                setLineChartData(preparedLineChart);
            }

            const preparedBarChart = prepareDailyLprCountBarChart();
            if (preparedBarChart) {
                setBarChartData(preparedBarChart);
            }
        }
    }, [dailyLprCountData]);

    Chart.register(...registerables);

    return (
        <div className={`count-bar-chart-wrapper ${isFullscreen ? 'fullscreen-container' : ''}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>

            <div className='graph-title' style={{ flex: '1 1 auto', minWidth: '200px', flexWrap: 'wrap' }}>
                Daily LPR Count 
            </div>

            <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    justifyContent: 'flex-end',
                    flex: '1 1 auto',
                    minWidth: '100px',
                }}>                
                <button className='toggle-chart-button' onClick={() => toggleChartType('month')}>
                {chartType ? (
                    <BiBarChartAlt2 size={24} title='Switch to Bar Chart' color='white' />
                ) : (
                    <BiLineChart size={24} title='Switch to Line Chart' color='white' />
                )}
                </button>

                <button
                className='toggle-chart-button'
                onClick={() => setIsFullscreen(prev => !prev)}
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                {isFullscreen ? (
                    <BiExitFullscreen size={24} color="white" />
                ) : (
                    <BiFullscreen size={24} color="white" />
                )}
                </button>

                <div className='count-chart-footer-percentage-container'>
                <div className='count-chart-footer-percentage-icon'>
                    {dailyVehiclePercentageIncrease > 0 ? (
                    <svg
                        style={{ color: '#32de84' }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-arrow-up-right-circle"
                        viewBox="0 0 16 16"
                    >
                        <path
                        fillRule="evenodd"
                        d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 10.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707l-4.096 4.096z"
                        />
                    </svg>
                    ) : (
                    <svg
                        style={{ color: '#EF5B5B' }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-arrow-down-right-circle"
                        viewBox="0 0 16 16"
                    >
                        <path
                        fillRule="evenodd"
                        d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 5.146a.5.5 0 1 0-.708.708L9.243 9.95H6.475a.5.5 0 1 0 0 1h3.975a.5.5 0 0 0 .5-.5V6.475a.5.5 0 1 0-1 0v2.768L5.854 5.146z"
                        />
                    </svg>
                    )}
                </div>
                <div
                    className='count-chart-footer-percentage-value'
                    style={{ color: dailyVehiclePercentageIncrease > 0 ? '#32de84' : '#EF5B5B' }}
                >
                    {dailyVehiclePercentageIncrease}%
                </div>
                </div>
            </div>
        </div>
            <div className='chart-total-container'>
                <div className='chart-total-pill'>
                    <div className="chart-total-label" style={{ color: '#FFC400' }}>
                    Total: {
                        Array.isArray(dailyLprCountData)
                        ? dailyLprCountData.reduce((acc, curr) => acc + (curr.total_count || 0), 0)
                        : 0
                    }
                    </div>
                </div>
            </div>
            <div className={dailyDataLoading ? 'dwell-monthly-bar-chart-container-loading' : ''}>
                {
                    chartType === 'line' ?
                        <Line
                            ref={lineChartRef}
                            data={lineChartData || { labels: [], datasets: [] }} // Fetch prepared data
                            options={getDailyLprCountLineChartOptions()}
                        />
                        :
                        <Bar
                            ref={barChartRef}
                            data={barChartData || { labels: [], datasets: [] }} // Fetch prepared data
                            options={getDailyLprCountBarChartOptions()}
                        
                        />

                }
                {/* <div className='dwell-chart-footer-container'>
                    <div className='dwell-chart-footer-label'>{dailyVehicles} vehicles detected for {selectedDayOfWeek} {selectedDay} of {getMonthName(selectedMonth)}</div>
                </div> */}
                {
                    dailyDataLoading && <div id="dwell-monthly-loading-overlay">
                        <BeatLoader color={'white'} loading={dailyDataLoading} size={15} />
                    </div>
                }

            </div>
        </div>
    )
}


export default LprDailyCountChart;